import type { GraphQLFormattedError } from 'graphql'
import {
  CombinedGraphQLErrors,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors'

/**
 * Reads an Apollo error: the one a hook or `client.query` returns or throws,
 * or the error link's `error`. Each function takes `unknown` so that a caller
 * never names an Apollo error class; Apollo 4 splits a failure into
 * `CombinedGraphQLErrors` (the response carried errors) and everything else
 * (`ServerError`, `ServerParseError`, a fetch that threw), which is the
 * transport failure.
 *
 * Twin of `apps/portal/src/lib/apolloErrors.ts`; change both.
 */

export const graphQLErrorsOf = (
  error: unknown,
): ReadonlyArray<GraphQLFormattedError> =>
  CombinedGraphQLErrors.is(error) ? error.errors : []

export const networkErrorOf = (error: unknown): Error | null =>
  error instanceof Error && !CombinedGraphQLErrors.is(error) ? error : null

/**
 * Whether the API refused with `code`. A non-2xx GraphQL response (the API
 * answers some refusals with a 403) arrives as `CombinedGraphQLErrors` when
 * served as `application/graphql-response+json`, and as a `ServerError`
 * holding only the raw body otherwise, so both are read.
 */
export const hasErrorCode = (error: unknown, code: string): boolean =>
  errorWithCode(error, code) !== undefined

export const errorWithCode = (
  error: unknown,
  code: string,
): GraphQLFormattedError | undefined =>
  [...graphQLErrorsOf(error), ...serverErrorBodyErrors(error)].find(
    (e) => e.extensions?.code === code,
  )

const GATEWAY_STATUSES = new Set([502, 503, 504])

/**
 * The API's answer behind a failed request, or null when none arrived: the
 * fetch threw, or the router answered 502, 503 or 504 in the API's place (on
 * Heroku a request that outlives the router keeps running). An answer is a
 * response that carried GraphQL errors or any other HTTP status; its message
 * is the API's first error, else Apollo's.
 */
export const apiAnswerOf = (error: unknown): { message: string } | null => {
  if (CombinedGraphQLErrors.is(error)) return { message: error.message }
  if (!ServerError.is(error) && !ServerParseError.is(error)) return null
  if (GATEWAY_STATUSES.has(error.statusCode)) return null
  return {
    message: serverErrorBodyErrors(error)[0]?.message || error.message,
  }
}

const serverErrorBodyErrors = (
  error: unknown,
): ReadonlyArray<GraphQLFormattedError> => {
  if (!ServerError.is(error)) return []
  try {
    const body: unknown = JSON.parse(error.bodyText)
    return body &&
      typeof body === 'object' &&
      'errors' in body &&
      Array.isArray(body.errors)
      ? (body.errors as GraphQLFormattedError[])
      : []
  } catch {
    return []
  }
}

/**
 * For a mutation whose `onError` has handled the failure, used as
 * `mutate(...).catch(afterOnError)`: resolves with no data, as Apollo 3 did,
 * where Apollo 4 rejects after calling `onError`. Without it the code after
 * an `await`, or a caller's own `try` / `.catch`, runs on a failure `onError`
 * already reported, and a call nobody awaits rejects unhandled.
 */
export const afterOnError = (error: Error) => ({ data: undefined, error })
